import { useEffect, useState } from "react";
import { api, hasToken, setToken } from "./lib/api";
import { isSmartContext, readChart, ready } from "./lib/smart";
import type { AppConfig, AssessmentPayload, SessionInfo, SmartSession } from "./lib/types";
import PatientList from "./components/PatientList";
import PatientView from "./components/PatientView";
import ManualEntry from "./components/ManualEntry";
import Integrations from "./components/Integrations";
import Audit from "./components/Audit";
import Login from "./components/Login";
import Logo from "./components/Logo";
import { useI18n } from "./lib/i18n";

function useHashRoute(): string {
  const [hash, setHash] = useState(window.location.hash || "#/");
  useEffect(() => {
    const on = () => setHash(window.location.hash || "#/");
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return hash;
}

export default function App() {
  const route = useHashRoute();
  const { t, lang, setLang } = useI18n();
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [smart] = useState(isSmartContext);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [checking, setChecking] = useState(!smart && hasToken());
  const [notice, setNotice] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // The phone menu closes whenever the page changes.
  useEffect(() => setMenuOpen(false), [route]);

  useEffect(() => {
    api.config().then(setConfig).catch(() => setConfig(null));
    if (!smart && hasToken()) {
      api.me().then(setSession).catch(() => setSession(null)).finally(() => setChecking(false));
    }
    const onOut = (e: Event) => {
      setSession(null);
      setNotice((e as CustomEvent<string>).detail);
    };
    window.addEventListener("pedob:signed-out", onOut);
    return () => window.removeEventListener("pedob:signed-out", onOut);
  }, [smart]);

  async function signOut() {
    await api.logout().catch(() => undefined);
    setSession(null);
    setNotice("Signed out.");
  }

  let page: JSX.Element;
  if (smart) {
    page = <SmartPatient config={config} onSession={setSession} />;
  } else if (checking) {
    page = <p className="muted">{t("loading")}</p>;
  } else if (!session) {
    page = <Login config={config} notice={notice} onSignedIn={(s) => { setNotice(null); setSession(s); }} />;
  } else if (route.startsWith("#/patient/")) {
    page = <PatientView key={route} patientId={decodeURIComponent(route.slice("#/patient/".length))} config={config} session={session} />;
  } else if (route === "#/manual") {
    page = <ManualEntry config={config} session={session} />;
  } else if (route === "#/integrations") {
    page = <Integrations config={config} session={session} />;
  } else if (route === "#/audit" && session.role === "admin") {
    page = <Audit config={config} />;
  } else {
    page = <PatientList />;
  }

  const nav = (href: string, label: string) => (
    <a href={href} aria-current={route === href || (href === "#/" && route.startsWith("#/patient/")) ? "page" : undefined}>
      {label}
    </a>
  );

  return (
    <div className="app">
      <header className={`topbar ${menuOpen ? "open" : ""}`}>
        <a className="brand" href="#/" aria-label="Pulse PedObesity home">
          <Logo height={32} />
          <span className="product">
            PedObesity UAE<small>WHO growth · SI units · AAP 2023 CPG</small>
          </span>
        </a>
        <button className="btn small menu-toggle" onClick={() => setMenuOpen(!menuOpen)} aria-expanded={menuOpen}
          aria-controls="topbar-menu" aria-label={t("menu")}>
          {menuOpen ? "✕" : "☰"}
        </button>
        {/* Inline on wide screens; a drop-down panel behind the menu button on phones. */}
        <div className="topbar-menu" id="topbar-menu">
          {!smart && session && (
            <nav className="nav" aria-label="Main">
              {nav("#/", t("patients"))}
              {nav("#/manual", t("manual"))}
              {nav("#/integrations", t("integrations"))}
              {session.role === "admin" && nav("#/audit", t("audit"))}
            </nav>
          )}
          <div className="spacer" />
          <button className="btn small" onClick={() => setLang(lang === "ar" ? "en" : "ar")}
            lang={lang === "ar" ? "en" : "ar"} aria-label="Switch interface language">
            {t("language")}
          </button>
          {smart && <span className="pill on">Launched from EHR (SMART)</span>}
          {config && (
            <span className={`pill ${config.ai.enabled ? "on" : ""}`} title={config.ai.model ?? "AI off: templates only"}>
              AI {config.ai.enabled ? "on" : "off"}
            </span>
          )}
          {session && (
            <span className="row user" style={{ gap: 8 }}>
              <span className="small">
                {session.display_name} <span className="muted">· {session.role}</span>
              </span>
              {!smart && (
                <button className="btn small" onClick={signOut}>
                  {t("signOut")}
                </button>
              )}
            </span>
          )}
        </div>
      </header>
      {config?.demo_mode && !smart && (
        <div className="banner" role="note">
          {t("demoBanner")}
        </div>
      )}
      <main>{page}</main>
    </div>
  );
}

function SmartPatient({ config, onSession }: { config: AppConfig | null; onSession: (s: SessionInfo) => void }) {
  const [state, setState] = useState<
    | { status: "loading"; step: string }
    | { status: "error"; msg: string }
    | { status: "ok"; data: AssessmentPayload; session: SmartSession; appSession: SessionInfo }
  >({ status: "loading", step: "Connecting to the EHR…" });

  useEffect(() => {
    (async () => {
      try {
        const client = await ready();
        setState({ status: "loading", step: "Verifying your EHR session…" });
        // The backend checks this token against the EHR before issuing an app session
        // scoped to the launched patient; the token itself is not stored.
        const login = await api.smartLogin({
          fhir_server: client.state.serverUrl,
          access_token: client.state.tokenResponse?.access_token ?? "",
          patient_id: client.patient.id ?? "",
          fhir_user: client.user.fhirUser ?? null,
        });
        setToken(login.token);
        onSession(login.session);
        setState({ status: "loading", step: "Reading the chart…" });
        const { bundle, session } = await readChart(client);
        const data = await api.assessFhir(bundle, session.fhirServer);
        setState({ status: "ok", data, session, appSession: login.session });
      } catch (e) {
        setState({ status: "error", msg: e instanceof Error ? e.message : String(e) });
      }
    })();
  }, [onSession]);

  if (state.status === "loading") return <p className="muted">{state.step}</p>;
  if (state.status === "error") return <p className="error">SMART session error: {state.msg}</p>;
  return (
    <PatientView patientId={state.data.patient.id} initial={state.data} smart={state.session} config={config}
      session={state.appSession} />
  );
}
